/**
 * RenameWatchlistModal Container Component
 *
 * Container for rename watchlist modal, managing form state and validation.
 * Replaces browser-native window.prompt() with an in-app modal that
 * renders consistently across web and native platforms.
 * @module features/scouting/components/RenameWatchlistModal
 */

import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import RenameWatchlistModalView from "./RenameWatchlistModalView";

import { buildWatchlistNameSchema } from "@/features/scouting/utils/watchlistNameRules";

/** Form values for watchlist name */
interface WatchlistNameFormValues {
  /** Watchlist name */
  name: string;
}

const NO_NAMES: readonly string[] = [];

/** Props for RenameWatchlistModalContainer */
interface RenameWatchlistModalContainerProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Current name of the watchlist being renamed */
  readonly currentName: string;
  /**
   * Names of the other lists the client knows (SE-8449): a case-variant of
   * one is refused inline before the round-trip. Pass the current list's own
   * name too if convenient — it is excluded here.
   */
  readonly existingNames?: readonly string[];
  /** Whether the rename operation is in progress */
  readonly isRenaming: boolean;
  /** Callback to close modal */
  readonly onClose: () => void;
  /** Callback when watchlist is renamed */
  readonly onSubmit: (newName: string) => void;
  /** Whether tapping the backdrop should dismiss the modal */
  readonly closeOnOverlayClick?: boolean;
}

/**
 * Container component for the rename watchlist modal
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is open
 * @param root0.currentName - Current watchlist name
 * @param root0.existingNames - Names of the client's other lists
 * @param root0.isRenaming - Whether rename is in progress
 * @param root0.onClose - Close handler
 * @param root0.onSubmit - Submit handler with new watchlist name
 * @param root0.closeOnOverlayClick - Whether backdrop taps dismiss the modal
 */
const RenameWatchlistModalContainer = ({
  isOpen,
  currentName,
  existingNames = NO_NAMES,
  isRenaming,
  onClose,
  onSubmit,
  closeOnOverlayClick = true,
}: RenameWatchlistModalContainerProps) => {
  // Renaming a list to a case-variant of its own name is not a duplicate.
  const schema = useMemo(() => {
    const ownKey = currentName.trim().toLowerCase();
    const others = existingNames.filter(
      name => name.trim().toLowerCase() !== ownKey
    );
    return yup.object().shape({ name: buildWatchlistNameSchema(others) });
  }, [currentName, existingNames]);

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<WatchlistNameFormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      name: currentName,
    },
  });

  const watchlistName = watch("name");

  const handleWatchlistNameChange = useCallback(
    (value: string) => {
      setValue("name", value, { shouldValidate: true });
    },
    [setValue]
  );

  const handleFormSubmit = useCallback(
    (data: WatchlistNameFormValues) => {
      onSubmit(data.name.trim());
    },
    [onSubmit]
  );

  const handleClose = useCallback(() => {
    reset({ name: currentName });
    onClose();
  }, [onClose, reset, currentName]);

  useEffect(() => {
    if (isOpen) {
      reset({ name: currentName });
    }
  }, [isOpen, currentName, reset]);

  return (
    <RenameWatchlistModalView
      isOpen={isOpen}
      errors={errors}
      isValid={isValid}
      isSubmitting={isRenaming}
      onClose={handleClose}
      onSubmit={handleSubmit(handleFormSubmit)}
      watchlistName={watchlistName}
      onWatchlistNameChange={handleWatchlistNameChange}
      closeOnOverlayClick={closeOnOverlayClick}
    />
  );
};

export default RenameWatchlistModalContainer;
